import React, { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { LIMIT } from './constants';
import { LogFilterForm } from './LogFilterForm';
import { LogTable } from './LogTable';
import { LogMobileList } from './LogMobileList';
import BusinessLogTab from './BusinessLogTab';
import {
  stdHeaderRow, stdPageTitle, stdPageSubtitle, stdTabGroup, stdTabBtn,
} from '../adminlayout/designStandard';
import {
  pageStyle, placeholderStyle,
  paginationStyle, pageBtnStyle,
} from './styles';

const INITIAL_FILTERS = {
  filterActor: '',
  filterFeature: '',
  filterMethod: 'ALL',
  filterStatus: '',
  filterDateFrom: '',
  filterDateTo: '',
  filterBodySearch: '',
};

export default function LogManagement() {
  const [view, setView] = useState('business');
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchLogs = useCallback(async (targetPage = 1, nextFilters = filters) => {
    setLoading(true);
    const params = new URLSearchParams({
      limit: String(LIMIT),
      offset: String((targetPage - 1) * LIMIT),
    });

    const {
      filterActor, filterFeature, filterMethod, filterDateFrom, filterDateTo, filterStatus, filterBodySearch,
    } = nextFilters;

    if (filterActor.trim()) params.set('actorSearch', filterActor.trim());
    if (filterMethod !== 'ALL') params.set('method', filterMethod);
    if (filterDateFrom) params.set('dateFrom', filterDateFrom);
    if (filterDateTo) params.set('dateTo', `${filterDateTo}T23:59:59`);
    if (filterStatus) params.set('statusGroup', filterStatus);
    if (filterBodySearch.trim()) params.set('bodySearch', filterBodySearch.trim());

    const featurePaths = filterFeature ? filterFeature.split(',') : [];
    if (featurePaths.length === 1) params.set('path', featurePaths[0]);

    const res = await apiClient.get(`/api/admin/logs?${params.toString()}`);
    setLoading(false);
    if (!res.ok) return;

    let rows = res.data?.list || [];
    if (featurePaths.length > 1) {
      rows = rows.filter((row) => featurePaths.some((fp) => String(row.path || '').startsWith(fp)));
    }

    setLogs(rows);
    setTotal(res.data?.total || rows.length);
    setPage(targetPage);
    setExpandedId(null);
  }, [filters]);

  useEffect(() => {
    if (view === 'api') fetchLogs(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  function handleSearch(e) {
    e.preventDefault();
    fetchLogs(1);
  }

  function handleReset() {
    setFilters(INITIAL_FILTERS);
    fetchLogs(1, INITIAL_FILTERS);
  }

  function handleFilterChange(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
    if (key === 'filterFeature') setPage(1);
  }

  function handleToggleExpand(logId) {
    setExpandedId((prev) => (prev === logId ? null : logId));
  }

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div style={pageStyle}>
      {/* ── 페이지 헤더: 제목/부제(좌) + 업무 이력/시스템 로그 알약 토글(우) — 직원 관리 표준 ── */}
      <div style={stdHeaderRow(isMobile)}>
        <div>
          <h2 style={stdPageTitle}>로그 관리</h2>
          <div style={stdPageSubtitle}>시스템·비즈니스 로그를 봅니다.</div>
        </div>
        <div style={stdTabGroup}>
          <button type="button" style={stdTabBtn(view === 'business')} onClick={() => setView('business')}>
            업무 이력
          </button>
          <button type="button" style={stdTabBtn(view === 'api')} onClick={() => setView('api')}>
            시스템/API 로그
          </button>
        </div>
      </div>

      {view === 'business' && <BusinessLogTab />}

      {view === 'api' && (
        <>
          <LogFilterForm
            filters={filters}
            onFilterChange={handleFilterChange}
            loading={loading}
            onSearch={handleSearch}
            onReset={handleReset}
            total={total}
            page={page}
            totalPages={totalPages}
          />

          {loading ? (
            <div style={{ ...placeholderStyle, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12 }}>
              로딩 중...
            </div>
          ) : isMobile ? (
            <LogMobileList logs={logs} expandedId={expandedId} onToggleExpand={handleToggleExpand} />
          ) : (
            <LogTable logs={logs} expandedId={expandedId} onToggleExpand={handleToggleExpand} />
          )}

          {totalPages > 1 && (
            <div style={paginationStyle}>
              <button style={pageBtnStyle(false)} disabled={page <= 1} onClick={() => fetchLogs(1)}>처음</button>
              <button style={pageBtnStyle(false)} disabled={page <= 1} onClick={() => fetchLogs(page - 1)}>이전</button>
              {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                let start = Math.max(1, page - 3);
                const end = Math.min(totalPages, start + 6);
                start = Math.max(1, end - 6);
                const p = start + i;
                if (p > totalPages) return null;
                return <button key={p} style={pageBtnStyle(p === page)} onClick={() => fetchLogs(p)}>{p}</button>;
              })}
              <button style={pageBtnStyle(false)} disabled={page >= totalPages} onClick={() => fetchLogs(page + 1)}>다음</button>
              <button style={pageBtnStyle(false)} disabled={page >= totalPages} onClick={() => fetchLogs(totalPages)}>마지막</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
